// Adaptive upload-concurrency controller (pure; used by useUploadManager's runUploadsAdaptive).

export interface ConcTuner {
  target: number;
  dir: 1 | -1;
  /** Smoothed (EWMA) throughput, bytes/s; 0 = no sample yet. */
  ewma: number;
  /** Smoothed throughput when `target` last changed — what a change is judged against. */
  ref: number;
  /** Consecutive samples clearly worse than `ref` (small files turn around after two, not on one noisy sample). */
  worse?: number;
}

/**
 * One controller step. Large files keep the classic dithering ±1 hill-climb (turn around on any 3% drop).
 * Small files: EWMA-smoothed rate, proportional steps, turn around only on a clear (>10%) drop, and —
 * when `probeUp` (HTTP/2 upload Worker: extra requests multiplex, they don't queue behind 6 sockets) —
 * keep probing upward while throughput holds, since more parallelism hides per-request latency.
 */
export function nextConcurrency(
  t: ConcTuner,
  rate: number,
  { lo, hi, small, probeUp }: { lo: number; hi: number; small: boolean; probeUp: boolean },
): ConcTuner {
  const clamp = (v: number) => Math.max(lo, Math.min(hi, v));
  if (!small) {
    if (t.ewma <= 0) return { ...t, ewma: rate, ref: rate };
    const dir = (rate < t.ewma * 0.97 ? -t.dir : t.dir) as 1 | -1;
    return { target: clamp(t.target + dir), dir, ewma: rate, ref: rate };
  }
  const ewma = t.ewma > 0 ? 0.5 * rate + 0.5 * t.ewma : rate;
  if (t.ref <= 0) return { ...t, ewma, ref: ewma, worse: 0 };
  const step = Math.max(1, Math.round(t.target * 0.25));
  const worse = ewma < t.ref * 0.9 ? (t.worse || 0) + 1 : 0;
  let dir = t.dir;
  if (worse >= 2) dir = (-t.dir) as 1 | -1; // clearly worse twice running → turn around
  else if (worse > 0 || (ewma <= t.ref * 1.05 && !(probeUp && dir > 0))) return { ...t, ewma, worse }; // hold
  const target = clamp(t.target + dir * step);
  return { target, dir, ewma, ref: target === t.target ? t.ref : ewma, worse: 0 };
}
